import axios from 'axios';
import { ProductType } from 'cart-model';
import { useTrans } from 'common-react';
import { Fund, PotentialProfit } from 'funds-model';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Currency,
  formatCurrencyWithoutTrailingZerosAndCode,
  Typography,
  Chip,
  CircularSpinner,
} from 'ui-components';
import { useFundEventLoggerWithContext } from '../../../model/FundEventsContext';
import VisibilityWrapper from '../../VisibilityWrapper/VisibilityWrapper';

import useStyles from './PotentialProfitSection.style';

interface PotentialProfitSectionProps {
  fund: Fund;
  initialAmount: number;
  monthlyAmount: number;
  productType: ProductType;
  isEntrepreneur?: boolean;
  index: number;
}

const PotentialProfitSection: React.FC<PotentialProfitSectionProps> = ({
  fund,
  initialAmount,
  monthlyAmount,
  productType,
  isEntrepreneur,
  index,
}) => {
  const [t] = useTranslation('select-recommended-funds');
  const { trans } = useTrans('select-recommended-funds');
  const c = useStyles({});
  const { logFundVisibilityIfShownFirstTime } = useFundEventLoggerWithContext();

  const [profit, setProfit] = useState<PotentialProfit | undefined>(undefined);

  useEffect(() => {
    const entrepreneurParam =
      isEntrepreneur != null && productType === ProductType.ikze ? isEntrepreneur : null;
    axios
      .get('/api/funds/potential-profit', {
        params: {
          fundId: fund.id,
          productCode: productType,
          initialAmount,
          monthlyAmount,
          isEntrepreneur: entrepreneurParam,
        },
      })
      .then((response) => {
        setProfit(response.data);
      });
  }, [fund.id, initialAmount, monthlyAmount, productType, isEntrepreneur]);

  return (
    <div className={c.wrapper}>
      {profit === undefined && <CircularSpinner />}
      {profit && profit.newFund && (
        <div className={c.newFundChipRow}>
          <Chip label={t('new-fund-chip')} className={c.newFundChipWrapper} />
        </div>
      )}

      {profit && !profit.newFund && (
        <>
          <Typography variant="body2" className={c.benefitsText} textColor="greys.60">
            {trans(
              'list.fund.benefits',
              'If you had invested <strong>{{first_payment_value}}</strong> PLN {{years_of_savings}} years ago and had payed in {{monthly_payments}} PLN monthly',
              {
                context: profit.monthsOfSaving > 11 ? 'years' : 'months',
                years_of_savings:
                  profit.monthsOfSaving > 11
                    ? profit.monthsOfSaving / 12
                    : profit.monthsOfSaving,
                first_payment_value: formatCurrencyWithoutTrailingZerosAndCode(initialAmount),
                monthly_payments: formatCurrencyWithoutTrailingZerosAndCode(monthlyAmount),
              }
            )}
          </Typography>
          <Currency
            value={profit.profit}
            valueSize='xl'
            codeSize='xl'
            valueFontWeight='extraBold'
            codeFontWeight='extraBold'
            valueClassName={c.fundItemValue}
            codeClassName={c.fundItemValueCode}
          />
        </>
      )}

      {profit && profit.newFund && (
        <Typography className={c.newFundInfo}>{t('new-fund-caption')}</Typography>
      )}

      <VisibilityWrapper
        setVisibility={(isVisible) =>
          isVisible && logFundVisibilityIfShownFirstTime(fund.id, productType, index)
        }
      >
      </VisibilityWrapper>
    </div>
  );
};

export default PotentialProfitSection;
